import { IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { GoogleService } from "@services/google.service";
import { GoogleMarkerService } from "@services/googleMarker.service";
import { VeriDate } from "@services/veriDate.service";
import { IGeoArea } from "@tms/_models/IGeoArea";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { GeofenceService } from "@services/geofence.service";
import { GeofenceType } from "@enums/GeofenceType";

export class PlannedRouteVisitService {

    static $inject = ["$http", "$q", "veriDate", "googleService", "googleMarkerService", "geofenceService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly veriDate: VeriDate,
        private readonly googleService: GoogleService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly geofenceService: GeofenceService
    ) {
    }

    updateDirectionsForVisits(visits: IPlannedRouteVisit[], updateTravelDistance: boolean = true, updateLinePoints: boolean = false, saveData: boolean = true) {
        const promises: angular.IPromise<IPlannedRouteVisit>[] = [];
        if (visits.length <= 1) {
            return this.$q.resolve(visits);
        }

        visits.forEach((visit, index) => {
            const lastVisit = visits[index - 1];
            if (!lastVisit) {
                promises.push(this.$q.resolve(visit));
                return;
            }

            if ((updateTravelDistance && !visit.TravelDistanceMetres) || (updateLinePoints && (!visit.LinePoints || visit.LinePoints.length === 2))) {
                const promise = this.updateDirectionsForVisit(lastVisit, visit, saveData);
                promises.push(promise);
            } else {
                promises.push(this.$q.resolve(visit));
            }
        });

        return this.$q.all(promises).then(visits => {
            return visits;
        });
    }

    updatePlannedRouteVisit(plannedRouteVisit: IPlannedRouteVisit): angular.IPromise<IPlannedRouteVisit> {
        return this
            .$http({
                url: `/api/tms/visits`,
                method: "POST",
                data: plannedRouteVisit
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlannedRouteVisit>) => {
                return response.data;
            });
    }

    calculateTotalDistanceKm(visits: IPlannedRouteVisit[]): { planned: number, actual: number } {
        let plannedDistanceAccKm: number = 0;
        let actualDistanceAccKm: number = 0;

        visits.forEach(visit => {
            plannedDistanceAccKm += visit.TravelDistanceMetres / 1000;
            actualDistanceAccKm += visit.ActualDistanceTraveled / 1000;
        });

        return { planned: plannedDistanceAccKm, actual: actualDistanceAccKm };
    }

    updateDirectionsForVisit(currentVisit: IPlannedRouteVisit, nextVisit: IPlannedRouteVisit, updateVisit: boolean = true): angular.IPromise<IPlannedRouteVisit> {
        return this.getRouteBetweenVisitLocations(currentVisit, nextVisit)
            .then((route) => {

                if (!route) {
                    return nextVisit;
                }

                const leg = route.legs[0];

                if (!nextVisit.TravelTime) {
                    const travelTime = leg.duration.value ? (leg.duration.value / 60) : 0;

                    nextVisit.TravelTime = this.veriDate.convertMinsToTimeSpan(travelTime);
                    nextVisit.travelTimeMins = travelTime;
                }

                if (!nextVisit.TravelDistanceMetres) {
                    nextVisit.TravelDistanceMetres = leg.distance.value;
                }

                if (!nextVisit.LinePoints || nextVisit.LinePoints.length <= 2) {
                    nextVisit.LinePoints = this.googleStepToLatLongArray(leg);
                }

                if (updateVisit) {
                    this.updatePlannedRouteVisit(nextVisit);
                }

                return nextVisit;
            })
            .catch(error => {
                console.error(error);
                return nextVisit;
            });
    }

    createRouteMarkerOptions(visit: IPlannedRouteVisit, index: number, max: number): IGoogleMapsMarkerOptions {

        let icon = "---";

        let iconValueSet = false;

        let markerType = "marker-event-pause";

        let label = visit.displayLabel;

        if (index === 0) {
            icon = "play_arrow";
            label = `Start: ${label}`;
            markerType = "marker-event-start";
            iconValueSet = true;
        }
        else if (index === max - 1) {
            icon = "stop"; //flag
            label = `End: ${label}`;
            markerType = "marker-event-end";
            iconValueSet = true;
        }

        const options = this.googleMarkerService.createEventMarkerOptions({ label: label, markerClass: markerType, subIcon: icon, showLabel: true });

        if (!iconValueSet) {
            options.labelContent = options.labelContent.replace(`<i class='material-icons marker-sub-icon'>${icon}</i>`, `<span class='marker-sub-icon text'>${index}</span>`);
        }

        options.visible = true;
        options.draggable = false;

        return options;
    }

    createUnplannedStopMarkerOptions(index: number): IGoogleMapsMarkerOptions {
        const options = this.googleMarkerService.createEventMarkerOptions({ label: "UnplannedStop", markerClass: "marker-event-end", subIcon: "flag", showLabel: true });

        //options.labelContent = options.labelContent.replace(`<i class='material-icons marker-sub-icon'>flag</i>`, `<span class='marker-sub-icon text'>${index}</span>`);

        options.visible = true;
        options.draggable = false;

        return options;
    }

    updateOnSiteTimeMins(visit: IPlannedRouteVisit): void {
        if (!visit.OnSiteTime) {
            visit.OnSiteTime = "00:00:00";
        }
       
        visit.onSiteTimeMins = this.veriDate.convertTimeSpanToMins(visit.OnSiteTime);
    }

    updateTimeProperties(visit: IPlannedRouteVisit): void {
        this.updateTravelTimeMins(visit);
        this.updatePlannedArrivalTimeSpan(visit);
        this.updatePlannedDepartureTimeSpan(visit);
        this.updateOnSiteTimeMins(visit);
    }

    updateTravelTimeMins(visit: IPlannedRouteVisit): void {
        if (!visit?.TravelTime) {
            return;
        }

        visit.travelTimeMins = this.veriDate.convertTimeSpanToMins(visit.TravelTime);
    }

    updatePlannedArrivalTimeSpan(visit: IPlannedRouteVisit): void {
        if (!visit?.PlannedArrivalTime) {
            return;
        }

        const plannedArrivalTimeMoD = this.veriDate.getMinuteOfDayFromDate(new Date(visit.PlannedArrivalTime));
        visit.plannedArrivalTimeSpan = this.veriDate.convertMinsToTimeSpan(plannedArrivalTimeMoD);
    }

    updatePlannedDepartureTimeSpan(visit: IPlannedRouteVisit): void {
        if (!visit?.PlannedDepartureTime) {
            return;
        }

        const plannedDepartureTimeMoD = this.veriDate.getMinuteOfDayFromDate(new Date(visit.PlannedDepartureTime));
        visit.plannedDepartureTimeSpan = this.veriDate.convertMinsToTimeSpan(plannedDepartureTimeMoD);
    }

    hasRouteStopLocation(visit: IPlannedRouteVisit): boolean {
        if (!visit?.RouteStopLocation) {
            return false;
        }

        return !!visit.RouteStopLocation.Latitude && !!visit.RouteStopLocation.Longitude;
    }

    getLatLngsForVisit(visit: IPlannedRouteVisit): google.maps.LatLng {
        let latLng: google.maps.LatLng;

        if (visit.RouteStopLocation || visit.RouteStopLocationJson) {

            if (!visit.RouteStopLocation && visit.RouteStopLocationJson) {
                visit.RouteStopLocation = JSON.parse(visit.RouteStopLocationJson) as IGeoArea;
            }

            if (visit.RouteStopLocation.GeofenceType == GeofenceType.Polygon || (visit.RouteStopLocation.Latitude == 0 && visit.RouteStopLocation.Longitude == 0)) {
                this.geofenceService.setPolygonGeofenceVisualCenter(visit.RouteStopLocation);
            }

            latLng = new google.maps.LatLng({
                lat: visit.RouteStopLocation.Latitude,
                lng: visit.RouteStopLocation.Longitude
            });
        }

        return latLng;
    }

    private getRouteBetweenVisitLocations(startLocation, endLocation): angular.IPromise<google.maps.DirectionsRoute> {
        if (!startLocation || !endLocation) {
            return this.$q.resolve(null);
        }

        const latLngFrom = this.getLatLngsForVisit(startLocation);
        const latLngTo = this.getLatLngsForVisit(endLocation);

        if (!latLngFrom || !latLngTo) {
            return this.$q.resolve(null);
        }

        return this.googleService.getDirections(latLngFrom, latLngTo).then((response) => {
            return response.routes[0];
        });
    }


    private googleStepToLatLongArray(leg: google.maps.DirectionsLeg): number[][] {
        var latLongs: number[][] = [];
        leg.steps.forEach(step => {
            step.path?.forEach(p => {
                latLongs.push([p.lat(), p.lng()]);
            });
        });

        return latLongs;
    }
}
